import { React, useState, useEffect } from 'react'
import { timeframes } from '../../utils/helpers'
import theme from '../../theme'
import WebSocketStatusBadge from './WebSocketStatusBadge';

// Result line for the SOLO postbox abort; result is null when the request failed.
export const postboxAbortSummary = (symbol, result) => {
    if (!result) return `${symbol}: postbox abort FAILED, nothing was changed. Check the connection and retry.`;
    const { aborted = 0 } = result;
    if (!aborted) return `${symbol}: no active postbox orders, nothing to abort.`;
    return `${symbol}: ${aborted} postbox order(s) aborted. Nothing more is traded for them, `
        + 'orders already at the broker are left as they are - check the position and open orders.';
};

// Timeframes socket field: JSON list of the rows the SOLO abort would hit.
// null = unknown (missing, lookup failed, unparsable).
export const parseActivePostboxOrders = (raw) => {
    if (typeof raw !== 'string') return null;
    try {
        const orders = JSON.parse(raw);
        return Array.isArray(orders) ? orders : null;
    } catch {
        return null;
    }
};

const ConfigPanel = ({
    selectedAsset,
    masterSwitch,
    handleSetFadeClick,
    handleToggleSpinDownClick,
    handleToggleActiveAssetClick,
    handleSetTimeframeSideClick,
    handleSetMasterSwitchClick,
    handleSetJoeRider,
    handleSetTacticalStart,
    handleSetTimeframeMetaAutoresolve = () => { },
    handleSetJ2F,
    handleSetUpdown,
    gridLocation,
    timeframesInfo,
    context,
    socketStatusLabel = null,
    socketConnected = false,
    tradingInstrumentActive = false,
    handleToggleTradingInstrumentClick = () => {},
    tradingAuto = false,
    handleToggleTradingAutoClick = () => {},
    instrumentSyncActive = false,
    handleToggleInstrumentSyncClick = () => {},
    handleAbortPostboxClick = null,
}) => {
    const activePostboxOrders = parseActivePostboxOrders(timeframesInfo.active_postbox_orders);
    const [timeframe, setTimeframe] = useState(timeframesInfo.j2f_target);

    useEffect(() => {
        if (
            timeframesInfo.active_timeframe !== timeframe &&
            !timeframesInfo.is_j2f && !timeframesInfo.is_02f
        ) {
            setTimeframe(timeframesInfo.j2f_target);
        }
    }, [timeframesInfo])

    const handleSubmitJ2F = (e) => {
        e.preventDefault();
        if (timeframe !== timeframesInfo.active_timeframe) {
            handleSetJ2F(timeframe, true);
        }
    };

    if (gridLocation === undefined) {
        gridLocation = 'sm:col-start-3 sm:col-end-3 sm:row-start-1 col-start-1 col-end-1 row-start-2 row-end-2';
    }

    // Toggle button config
    const toggleButtons = [
        {
            label: selectedAsset.isAssetActive ? 'ACTIVE' : 'INACTIVE',
            active: selectedAsset.isAssetActive,
            onClick: handleToggleActiveAssetClick,
            activeColor: 'bg-olive-100 text-olive-500',
            inactiveColor: 'bg-olive-600 text-white',
        },
        {
            label: timeframesInfo.is_algo_active ? 'TRADE' : 'FADE',
            active: timeframesInfo.is_algo_active,
            onClick: () => handleSetFadeClick(timeframesInfo.is_algo_active),
            activeColor: 'bg-olive-100 text-olive-500',
            inactiveColor: 'bg-olive-100 text-olive-500',
        },
        {
            label: timeframesInfo.is_spin_down ? 'SHABBAT' : 'NO SHABBAT',
            active: timeframesInfo.is_spin_down,
            onClick: handleToggleSpinDownClick,
            activeColor: 'bg-olive-600 text-white',
            inactiveColor: 'bg-olive-100 text-olive-500',
        },
        {
            label: timeframesInfo.is_joe_rider ? 'DJANGO UNCHAINED' : 'DJANGO CHAINED',
            active: timeframesInfo.is_joe_rider,
            onClick: () => handleSetJoeRider(!timeframesInfo.is_joe_rider),
            activeColor: 'bg-olive-600 text-white',
            inactiveColor: 'bg-olive-100 text-olive-500',
        },
        {
            label: timeframesInfo.is_tactical_start ? 'TACTICAL' : 'BREAKOUT',
            active: timeframesInfo.is_tactical_start,
            onClick: () => handleSetTacticalStart(!timeframesInfo.is_tactical_start),
            activeColor: 'bg-olive-600 text-white',
            inactiveColor: 'bg-olive-100 text-olive-500',
        },
        {
            label: timeframesInfo.is_updown ? 'UPDOWN' : 'REGULAR',
            active: timeframesInfo.is_updown,
            onClick: () => handleSetUpdown(!timeframesInfo.is_updown),
            activeColor: 'bg-olive-600 text-white',
            inactiveColor: 'bg-olive-100 text-olive-500',
        },
        {
            label: tradingInstrumentActive ? 'TRADING' : 'SCANNER',
            active: tradingInstrumentActive,
            onClick: handleToggleTradingInstrumentClick,
            activeColor: 'bg-olive-600 text-white',
            inactiveColor: 'bg-olive-100 text-olive-500',
        },
        // AUTO: only Trend Extension "in play" trades; SEMI (default): regular TOS.
        // A SCANNER instrument never trades, so the switch is hidden there.
        ...(tradingInstrumentActive ? [{
            label: tradingAuto ? 'AUTO' : 'SEMI',
            active: tradingAuto,
            onClick: handleToggleTradingAutoClick,
            activeColor: 'bg-olive-600 text-white',
            inactiveColor: 'bg-olive-100 text-olive-500',
        }] : []),
        // Off by default. Switching it on seeds every other account carrying this
        // instrument from this one, and from then on manual changes travel both
        // ways between all of them. Level quantities stay individual.
        {
            label: instrumentSyncActive ? 'SYNC ENABLED' : 'SYNC DISABLED',
            active: instrumentSyncActive,
            onClick: handleToggleInstrumentSyncClick,
            activeColor: 'bg-olive-600 text-white',
            inactiveColor: 'bg-olive-100 text-olive-500',
        },
        // Off by default: a t/m/s alert is expected to carry timeframe_meta.
        // Enabling this accepts alerts that name the level instead, resolving
        // whichever timeframe that level is currently bound to.
        {
            label: timeframesInfo.is_timeframe_meta_autoresolve ? 'TF AUTORESOLVE' : 'TF STRICT',
            active: timeframesInfo.is_timeframe_meta_autoresolve,
            onClick: () => handleSetTimeframeMetaAutoresolve(
                !timeframesInfo.is_timeframe_meta_autoresolve
            ),
            activeColor: 'bg-olive-600 text-white',
            inactiveColor: 'bg-olive-100 text-olive-500',
        },
        // One-shot action, not a toggle: aborts this instrument's postbox queue
        // without restarting the executor. Disabled only on a known-empty queue:
        // an unknown one (load failed) must never lock the operator out.
        ...(handleAbortPostboxClick ? [{
            label: 'ABORT POSTBOX',
            active: false,
            disabled: activePostboxOrders?.length === 0,
            onClick: handleAbortPostboxClick,
            activeColor: '',
            inactiveColor: 'bg-red-50 text-red-600',
            footer: activePostboxOrders?.length > 0 && (
                <ul data-testid="active-postbox-orders" className="px-1 pt-1 space-y-0.5 text-xs text-red-600">
                    {activePostboxOrders.map((o) => (
                        <li key={o.id} className="flex justify-between gap-2">
                            <span className="font-semibold">{o.action}{o.timeframe ? ` ${o.timeframe}` : ''}</span>
                            <span>{o.status}{o.created_at ? ` · ${new Date(o.created_at).toLocaleTimeString()}` : ''}</span>
                        </li>
                    ))}
                </ul>
            ),
        }] : []),
    ];

    return (
        <div className={`${gridLocation}`}>
            <div
                className={`ml-4 ${theme.cardBg} rounded-2xl ${theme.cardBorder} overflow-hidden`}
                style={{
                    boxShadow: '0 4px 24px rgba(0, 0, 0, 0.06), 0 1px 4px rgba(0, 0, 0, 0.04)',
                }}
            >
                {/* Header */}
                <div className="px-4 pt-3 pb-2">
                    <div className="flex items-center justify-between gap-3">
                        <h3 className={`text-xs font-semibold ${theme.textMuted} tracking-widest uppercase`}>Controls</h3>
                        {socketStatusLabel && (
                            <WebSocketStatusBadge
                                connected={socketConnected}
                            />
                        )}
                    </div>
                </div>

                {/* Side buttons */}
                {/* <div className="px-3 pb-2">
                    <div className="grid grid-cols-2 gap-2">
                        <button
                            onClick={() => handleSetTimeframeSideClick('buy', timeframesInfo.active_timeframe)}
                            className="py-2.5 rounded-xl font-bold text-sm text-white bg-emerald-500 transition-all duration-200 hover:bg-emerald-600 hover:shadow-md active:scale-[0.97]"
                        >
                            BUY
                        </button>
                        <button
                            onClick={() => handleSetTimeframeSideClick('sell', timeframesInfo.active_timeframe)}
                            className="py-2.5 rounded-xl font-bold text-sm text-white bg-red-500 transition-all duration-200 hover:bg-red-600 hover:shadow-md active:scale-[0.97]"
                        >
                            SELL
                        </button>
                    </div>
                </div> */}

                {/* Toggle buttons */}
                <div className="px-3 pb-3 space-y-1.5">
                    {toggleButtons.map((btn, i) => (
                        <div key={i}>
                            <button
                                onClick={btn.onClick}
                                disabled={btn.disabled}
                                className={`w-full py-2 rounded-xl text-sm font-semibold transition-all duration-200
                ${btn.disabled ? 'opacity-40 cursor-not-allowed' : 'hover:shadow-sm active:scale-[0.98]'}
                ${btn.active ? btn.activeColor : btn.inactiveColor}
              `}
                            >
                                {btn.label}
                            </button>
                            {btn.footer}
                        </div>
                    ))}
                </div>
            </div>
        </div>
    )
}

export default ConfigPanel
